import type { StreamObject } from '../components/ai-chat-messages/type';
import { mergeStreamContent } from './stream-objects';

// Transport guards only. Model-specific context budgeting happens centrally
// in the backend together with every other canonical context source.
const MAX_TRANSCRIPT_MESSAGES = 256;
const MAX_TRANSCRIPT_LENGTH = 256_000;
const MAX_LABEL_LENGTH = 72;

type AIChatSelectionProps = Record<string, unknown> & {
  messages?: unknown;
  parentBlockId?: unknown;
};

type VisibleChatMessage = {
  role: 'user' | 'assistant';
  content: string;
};

function readableText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function visibleMessage(value: unknown): VisibleChatMessage | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  if (record.role !== 'user' && record.role !== 'assistant') return null;
  if (
    record.messageType === 'tool_approval_request' ||
    record.messageType === 'tool_approval_response' ||
    record.messageType === 'tool_result'
  ) {
    return null;
  }

  const streamObjects = Array.isArray(record.streamObjects)
    ? (record.streamObjects as StreamObject[])
    : [];
  const content =
    (streamObjects.length ? mergeStreamContent(streamObjects) : '') ||
    readableText(record.content);
  if (!content) return null;
  return { role: record.role, content };
}

function parseMessages(value: unknown): VisibleChatMessage[] {
  if (typeof value !== 'string' || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value) as unknown;
    return Array.isArray(parsed)
      ? parsed.flatMap(message => {
          const visible = visibleMessage(message);
          return visible ? [visible] : [];
        })
      : [];
  } catch {
    return [];
  }
}

function truncate(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value;
  return `${value.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
}

function truncateHeadTail(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value;
  const marker = '\n[… transport guard …]\n';
  const available = Math.max(0, maxLength - marker.length);
  const headLength = Math.floor(available * 0.2);
  const tailLength = available - headLength;
  return `${value.slice(0, headLength).trimEnd()}${marker}${value
    .slice(-tailLength)
    .trimStart()}`;
}

export type AIChatSelectionContext = {
  labelSuffix: string | null;
  transcript: string | null;
  conversationParentId: string | null;
};

/**
 * Produces read-only analysis context for explicitly selected AI chat blocks.
 * Tool payloads and approvals are deliberately excluded: selection grants
 * access to visible conversation text, not authority to replay actions.
 */
export function getAIChatSelectionContext(
  props: AIChatSelectionProps | null | undefined
): AIChatSelectionContext {
  const messages = parseMessages(props?.messages).slice(
    -MAX_TRANSCRIPT_MESSAGES
  );
  const firstUserMessage = messages.find(message => message.role === 'user');
  const labelSuffix = firstUserMessage
    ? truncate(firstUserMessage.content.replace(/\s+/g, ' '), MAX_LABEL_LENGTH)
    : null;
  const transcript = messages.length
    ? truncateHeadTail(
        messages
          .map(
            message =>
              `${message.role === 'user' ? 'User' : 'Assistant'}: ${message.content}`
          )
          .join('\n'),
        MAX_TRANSCRIPT_LENGTH
      )
    : null;

  return {
    labelSuffix,
    transcript,
    conversationParentId:
      typeof props?.parentBlockId === 'string' && props.parentBlockId
        ? props.parentBlockId
        : null,
  };
}
